import { Injectable } from '@angular/core';

import { FixtureType } from '@cds/betting-offer';
import { CountsFixturesResponse } from '@cds/betting-offer/domain-specific';
import { TagType } from '@cds/betting-offer/tags';
import { CountsFixturesRequest, FixtureState, OfferMapping, SortByCriteria } from '@cds/query-objects';
import { BettingOfferApi } from '@frontend/sports/betting-offer/feature/offer-service';
import { LiveNavigationConfig } from '@frontend/sports/common/client-config-data-access';
import { filterDefined } from '@frontend/sports/common/core/utils/extended-types';
import { filterSports } from '@frontend/sports/host-app/sports-product/feature/utils';
import { UserService } from '@frontend/sports/user/feature';
import { toString } from 'lodash-es';
import { Observable, debounceTime, shareReplay, switchMap, timer } from 'rxjs';

import { LiveEventsNavigationStateService } from './live-events-navigation-state.service';

@Injectable({ providedIn: 'root' })
export class LiveEventsNavigationRefreshService {
    refresh$: Observable<CountsFixturesResponse>;
    reload$: Observable<CountsFixturesResponse>;

    constructor(
        private stateService: LiveEventsNavigationStateService,
        private liveNavConfig: LiveNavigationConfig,
        private bettingContent: BettingOfferApi,
        private userService: UserService,
    ) {
        this.registerRefresh();
        this.registerReload();
    }

    private registerRefresh(): void {
        const intervalInMs = this.liveNavConfig.pollingInterval * 1000;

        this.refresh$ = timer(intervalInMs, intervalInMs).pipe(
            debounceTime(2000),
            filterSports(),
            switchMap(() => this.getData()),
            shareReplay({ bufferSize: 1, refCount: true }),
        );
    }

    private registerReload(): void {
        this.reload$ = this.userService.onAuthenticationChange$.pipe(switchMap(() => this.getData()));
    }

    private getData(): Observable<CountsFixturesResponse> {
        const request: CountsFixturesRequest = {
            fixtureCountsRequest: {
                state: FixtureState.Live,
                tagTypes: [TagType.Sport, TagType.Region, TagType.Tournament, TagType.Competition].join(','),
                extendedTags: [TagType.Sport, TagType.Region, TagType.Tournament, TagType.Competition].join(','),
                sortBy: SortByCriteria.Tags,
            },
            fixtureRequest: {
                fixtureTypes: FixtureType.Standard,
                state: FixtureState.Live,
                offerMapping: OfferMapping.MainMarkets,
                sortBy: SortByCriteria.FixtureStage,
                excludeCompetitionIds: toString(Array.from(this.stateService.state.collapsedLeagues)),
                sportIds: toString(Array.from(this.stateService.state.expandedSports)),
            },
        };

        return this.bettingContent.getCountsFixtures(request).pipe(filterDefined());
    }
}
